import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useParams, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BedDouble,
  Briefcase,
  Building2,
  Car,
  ChevronRight,
  Globe,
  Search,
  Share2,
  Sparkles,
  X,
} from "lucide-react";
import spaceManagementService from "src/services/host-apiServices/spaceManagementService";
import billingService from "src/services/billingService";
import { formatDurationType } from "src/utils/bookingTimeframeUtils";
import { generateSpaceName, getEventName } from "src/utils/spaceNamingUtils";
import * as localService from "src/services/localService";
import SEO from "src/components/SEO/SEO";
import ReusableBookingModal from "src/components/ReusableBookingModal";
import PageHeader from "src/components/PageHeader";
import SortDropdown from "src/components/SortDropdown";
import Pagination from "src/components/Pagination";
import GridView from "src/screens/host-experience/host-spaces/components/GridView";
import SpaceCardSkeleton from "src/screens/host-experience/host-spaces/components/SpaceCardSkeleton";
import { Input } from "src/shadcn/ui/input";
import { Button } from "src/shadcn/ui/button";
import logger from "../../utils/logger";


const SPACES_PER_CATEGORY = 12;

const EMPTY_ORG = {
  id: null,
  name: "",
  logoUrl: "",
  domain: "",
  city: "",
};

// Categories that are host-only tooling, never shown as bookable sections
const HOST_ONLY_CATEGORY_CODES = new Set(["PREMIUM_HOST_SERVICES", "HOSTSAAS"]);

const CATEGORY_META = {
  WORK: { icon: Briefcase },
  STAY: { icon: BedDouble },
  PLAY: { icon: Sparkles },
  MOVE: { icon: Car },
};

// Search results only carry the truncated organization payload — use just the fields available there
const normalizeOrg = orgData => {
  if (!orgData) return { ...EMPTY_ORG };
  return {
    id: orgData.id ?? null,
    name: orgData.name || "",
    logoUrl: orgData.logoUrl || "",
    domain: orgData.domain || "",
    city: orgData.city || "",
  };
};

// In-memory cache so in-app navigation (e.g. breadcrumbs) keeps the org without refetching
const orgCache = {};

const getPrimaryImageFromMedia = space => {
  if (space?.media?.images?.length) {
    const first = space.media.images[0];
    if (typeof first === "string") return first;
    if (first?.url) return first.url;
  }
  if (space?.image) return space.image;
  return "/img/placeholder-space.jpg";
};

// Mirror of SpacesSearchView's mapSpaceForDisplay — search results carry no `name`,
// so the display name is derived via generateSpaceName (fixes "Untitled Space" cards)
const mapSpaceForDisplay = space => {
  if (!space) return null;
  const firstInstance = space.instances?.[0];
  const buildingAddress = firstInstance?.location?.buildingAddress;

  const locationParts = [
    buildingAddress?.streetAddress,
    buildingAddress?.city,
    buildingAddress?.region,
    buildingAddress?.country,
  ].filter(Boolean);

  const location =
    locationParts.join(", ") ||
    firstInstance?.location?.name ||
    space.cityOrTown ||
    space.addressContains ||
    "Location unavailable";

  const spaceCurrency = space.currency || "KES";

  const pricing = (() => {
    if (space.ratePrice) {
      const unit = (space.duration || space.durationUnit || "").toString();
      return `${spaceCurrency} ${(space.ratePrice || 0).toLocaleString()} ${formatDurationType(unit)}`;
    }
    if (space.propertyPrice?.amount) {
      return `${spaceCurrency} ${(space.propertyPrice.amount || 0).toLocaleString()}`;
    }
    return null;
  })();

  const currentUser = localService.getUser();
  const hostOrgName = currentUser?.organization?.name || currentUser?.organizationName;
  const spaceWithOrg = { ...space, organizationName: space.organizationName || hostOrgName };
  // PLAY services show their event name (stored as the instance name/title)
  const eventName = getEventName(space, firstInstance);
  const displayName = eventName || (space.hostDisplayName
    ? `${space.subCategory?.name || space.subCategoryName || space.description || "Space"} - ${space.hostDisplayName}`
    : generateSpaceName(spaceWithOrg, firstInstance, true));

  return {
    ...space,
    name: displayName,
    displayName,
    propertyTitle: space.propertyTitle || displayName,
    image: getPrimaryImageFromMedia(space),
    location,
    pricing,
    coordinates:
      buildingAddress?.latitude && buildingAddress?.longitude
        ? { latitude: buildingAddress.latitude, longitude: buildingAddress.longitude }
        : firstInstance?.location?.coordinates || null,
    instanceCount: space.availableInstancesCount || space.instances?.length || 0,
    hostDisplayName: space.hostDisplayName,
    hostDisplayImage: space.hostDisplayImage,
  };
};

// ---------------------------------------------
// Category section: heading + grid of spaces
// ---------------------------------------------
const CategorySection = ({ category, spaces, onOpenSpace, onExpand }) => {
  const meta = CATEGORY_META[category.code] || { icon: Building2 };
  const Icon = meta.icon;
  const showViewAll = spaces.length > 1;
  const subcategoryNames = (category.subcategories || [])
    .map(sub => sub.name)
    .filter(Boolean)
    .slice(0, 4)
    .join(" · ");

  return (
    <section className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 flex-shrink-0">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">{category.name}</h2>
            {subcategoryNames && <p className="mt-0.5 text-sm text-gray-500">{subcategoryNames}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-2.5 py-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full">
            {spaces.length} {spaces.length === 1 ? "space" : "spaces"}
          </span>
          {showViewAll && (
            <button
              onClick={() => onExpand && onExpand(category)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <GridView spaces={spaces} onSelectSpace={onOpenSpace} onBookSpace={onOpenSpace} />
    </section>
  );
};

// ---------------------------------------------
// Expanded category results — Booking.jsx-style header + grid/list + pagination
// ---------------------------------------------
const CATEGORY_SORT_OPTIONS = [
  { field: "createdAt", label: "Created" },
  { field: "ratePrice", label: "Price" },
];

const CategorySearchResults = ({ category, orgId, subtitle, onOpenSpace }) => {
  const [spaces, setSpaces] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt:desc");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const fetchSpaces = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams = {
        hostOrganizationId: orgId,
        categoryId: category.id,
        pageNumber: Math.max(0, currentPage - 1),
        pageSize: ITEMS_PER_PAGE,
        paged: true,
        status: "PUBLISHED",
        sort: sortBy.replace(":", ","),
      };
      const keyword = activeSearch.trim();
      if (keyword) {
        baseParams.spaceName = keyword;
        baseParams.addressContains = keyword;
      }

      let response = null;
      if (localService.getAuthToken()) {
        try {
          response = await spaceManagementService.searchPublicSpaces(baseParams);
        } catch (err) {
          // An unresolved market is not a transport failure — retrying against
          // the public endpoint would hit the same guard. Propagate so the
          // caller can show the country picker.
          if (err?.code === "MARKET_UNRESOLVED" || err?.name === "MarketUnresolvedError") throw err;
          logger.warn("Category search (authenticated) failed, falling back to public:", err);
        }
      }
      if (!response) {
        response = await spaceManagementService.searchPublicSpacesUnauthenticated(baseParams);
      }

      const content = Array.isArray(response?.data?.content)
        ? response.data.content
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setSpaces(content.map(mapSpaceForDisplay).filter(Boolean));
      setTotalCount(response?.data?.totalElements ?? content.length);
    } catch (err) {
      logger.error("Category search failed:", err);
      setSpaces([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [orgId, category.id, currentPage, activeSearch, sortBy]);

  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  const handleSearchSubmit = e => {
    e.preventDefault();
    setCurrentPage(1);
    setActiveSearch(searchValue);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setActiveSearch("");
    setCurrentPage(1);
  };

  return (
    <div>
      <PageHeader
        title={category.name}
        totalCount={totalCount}
        subtitle={subtitle}
        rightContent={<SortDropdown options={CATEGORY_SORT_OPTIONS} value={sortBy} onChange={setSortBy} />}
        bottomContent={
          <div className="pt-2 pb-2">
            <form onSubmit={handleSearchSubmit} className="w-full">
              <div className="relative">
                <Input
                  type="search"
                  placeholder="Search by space name, address..."
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  className="pl-4 pr-12 bg-white [appearance:textfield] [::-webkit-search-decoration]:hidden [::-webkit-search-cancel-button]:hidden [::-webkit-search-results-button]:hidden [::-webkit-search-results-decoration]:hidden"
                />
                <button
                  type={searchValue ? "button" : "submit"}
                  onClick={searchValue ? handleClearSearch : undefined}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 transition"
                  aria-label={searchValue ? "Clear search" : "Search"}
                >
                  {searchValue ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>
        }
      />

      <div className="mt-6">
        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, idx) => (
              <SpaceCardSkeleton key={idx} />
            ))}
          </div>
        ) : spaces.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">
              {activeSearch ? "No spaces found. Try adjusting your search." : "No spaces found in this category."}
            </p>
          </div>
        ) : (
          <GridView spaces={spaces} onSelectSpace={onOpenSpace} onBookSpace={onOpenSpace} />
        )}
      </div>

      {totalCount > ITEMS_PER_PAGE && (
        <div className="flex justify-center mt-6">
          <Pagination
            className="pagination-bar"
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={ITEMS_PER_PAGE}
            siblingCount={1}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

const HostProfileSkeleton = () => (
  <div className="pb-12 min-h-screen bg-slate-50/50">
    <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
      {/* Header shimmer */}
      <div className="space-y-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-10 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-3 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-28 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-3 bg-gray-200 animate-pulse rounded" />
          <div className="h-4 w-32 bg-gray-200 animate-pulse rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-8 w-48 bg-gray-200 animate-pulse rounded-lg" />
          <div className="h-4 w-32 bg-gray-200 animate-pulse rounded-md" />
        </div>
      </div>

      {/* Card grid shimmer */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <SpaceCardSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

const HostProfilePage = () => {
  const { hostId } = useParams();
  const orgId = Number(hostId);
  const isValidOrgId = Number.isInteger(orgId) && orgId > 0;
  const location = useLocation();
  const history = useHistory();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [organization, setOrganization] = useState(() =>
    normalizeOrg(orgCache[orgId] || location.state?.organization || null)
  );
  const [sections, setSections] = useState([]); // [{ category, spaces }]
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [bookingSpace, setBookingSpace] = useState(null);

  useEffect(() => {
    setExpandedCategory(null);
  }, [location.pathname, location.search]);

  // Category lives in the URL (?category=id) so breadcrumbs/back navigation collapse it naturally
  const categoryParam = Number(new URLSearchParams(location.search).get("category"));

  useEffect(() => {
    if (!categoryParam || sections.length === 0) return;
    const match = sections.find(section => section.category.id === categoryParam);
    setExpandedCategory(match ? match.category : null);
  }, [categoryParam, sections]);

  const handleExpandCategory = category => {
    history.push(
      {
        pathname: `/hosts/${orgId}`,
        search: `?category=${category.id}`,
      },
      location.state
    );
    setExpandedCategory(category);
  };

  // The org object travels with the navigation from search results (truncated payload:
  // name, logoUrl, domain, city). No org fetch here — only use what was passed.
  useEffect(() => {
    const org = normalizeOrg(location.state?.organization || orgCache[orgId] || null);
    orgCache[orgId] = org;
    setOrganization({ ...org, id: orgId });
  }, [location.state, orgId]);

  // Load END_USER service categories so each section mirrors a real category.
  // Use the categories stored in Redux after login (same source as SpacesSearchView),
  // falling back to the public endpoint for unauthenticated/empty cases.
  const reduxCategories = useSelector(state => state.dashboard?.serviceCategories || []);

  const loadCategories = useCallback(async () => {
    let data = [];
    if (localService.getAuthToken() && reduxCategories.length > 0) {
      data = reduxCategories;
    }

    if (data.length === 0) {
      try {
        const response = await billingService.getPublicServiceCategories({ orderBy: "name:asc" });
        if (Array.isArray(response?.data)) data = response.data;
        else if (Array.isArray(response?.data?.content)) data = response.data.content;
        else if (Array.isArray(response?.data?.data)) data = response.data.data;
      } catch (err) {
        logger.error("Host profile: failed to load service categories:", err);
        return [];
      }
    }

    return data
      .filter(category => {
        if (category?.type) return String(category.type).toUpperCase() === "END_USER";
        return !HOST_ONLY_CATEGORY_CODES.has(String(category?.code || "").toUpperCase());
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [reduxCategories]);

  const loadSpacesForCategory = useCallback(
    async category => {
      const searchParams = {
        hostOrganizationId: orgId,
        categoryId: category.id,
        pageNumber: 0,
        pageSize: SPACES_PER_CATEGORY,
        paged: true,
        status: "PUBLISHED",
      };

      try {
        // Use the authenticated search when logged in, fall back to the public endpoint otherwise
        let response = null;
        if (localService.getAuthToken()) {
          try {
            response = await spaceManagementService.searchPublicSpaces(searchParams);
          } catch (err) {
            // Unresolved market — the public endpoint would hit the same guard,
            // so surface it to the caller rather than retrying.
            if (err?.code === "MARKET_UNRESOLVED" || err?.name === "MarketUnresolvedError") throw err;
            logger.warn(
              `Host profile: authenticated search failed for ${category.code}, falling back to public:`,
              err
            );
          }
        }
        if (!response) {
          response = await spaceManagementService.searchPublicSpacesUnauthenticated(searchParams);
        }

        const content = response?.data?.content;
        if (!Array.isArray(content)) throw new Error("Unexpected spaces response");
        return { category, spaces: content.map(mapSpaceForDisplay).filter(Boolean) };
      } catch (err) {
        logger.error(`Host profile: failed to load spaces for category ${category.code}:`, err);
        return { category, spaces: [] };
      }
    },
    [orgId]
  );

  const loadSections = useCallback(async () => {
    if (!isValidOrgId) {
      setSectionsLoading(false);
      return;
    }
    setSectionsLoading(true);
    setError(null);

    try {
      const categories = await loadCategories();
      const withOrg = categories.map(category => ({
        ...category,
        hostOrganizationId: orgId,
        hostOrgName: organization.name || undefined,
      }));
      const results = await Promise.all(withOrg.map(loadSpacesForCategory));
      setSections(results.filter(section => section.spaces.length > 0));
    } catch (err) {
      logger.error("Host profile: failed to load sections:", err);
      setError("We couldn't load this host's spaces right now. Please try again.");
    } finally {
      setSectionsLoading(false);
    }
  }, [isValidOrgId, loadCategories, loadSpacesForCategory, organization.name]);

  useEffect(() => {
    loadSections();
  }, [loadSections, retryKey]);

  useEffect(() => {
    document.title = organization.name ? `${organization.name} · Spaces on Spacia` : "Host Profile · Spacia";
  }, [organization.name]);

  const totalSpaces = sections.reduce((sum, section) => sum + section.spaces.length, 0);

  const profileUrl = `${window.origin}/hosts/${orgId}`;
  const spaceListings = sections
    .flatMap(section => section.spaces)
    .slice(0, 12)
    .map(space => ({
      "@type": "ListItem",
      position: space.id,
      name: space.name || "Space",
      url: `${window.origin}/space/${space.id}`,
    }));

  const handleShare = async () => {
    const url = `${window.origin}/hosts/${orgId}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: organization.name || "Host",
          text: `Check out ${organization.name || "this host"} on Spacia`,
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        window.alert("Link copied to clipboard!");
      }
    } catch {
      // User dismissed share sheet — no-op
    }
  };

  const locationLine = organization.city || "";
  const websiteUrl = organization.domain || "";

  const user = localService.getUser();
  const isHost = localService.shouldSeeHostExperience ? localService.shouldSeeHostExperience() : false;
  const homeHref = user ? (isHost ? "/host/dashboard" : "/dashboard") : "/home";
  // Breadcrumb host label: fallback to #id when the org wasn't passed with the navigation
  const hostLabel = organization.name || `#${hostId}`;

  // Continue the originating search screen's breadcrumb trail (Home → Search Results → Host)
  const fromBreadcrumbs = location.state?.fromBreadcrumbs;

  const defaultBreadcrumbs = [
    ...(fromBreadcrumbs && fromBreadcrumbs.length > 0
      ? fromBreadcrumbs
      : [
          { label: "Home", path: homeHref },
          { label: "Spaces & Services", path: "/filterprops" },
        ]),
    { label: hostLabel, path: expandedCategory ? `/hosts/${orgId}` : undefined },
  ];
  if (expandedCategory) {
    defaultBreadcrumbs.push({ label: expandedCategory.name });
  }

  if (!isValidOrgId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-400" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Host not found</h1>
          <p className="mt-2 text-slate-500">This link doesn't point to a valid host profile.</p>
          <Link
            to="/filterprops"
            className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Browse all spaces
          </Link>
        </div>
      </div>
    );
  }

  if (sectionsLoading) {
    return <HostProfileSkeleton />;
  }

  return (
    <div className="pb-12 min-h-screen bg-slate-50/50">
      <SEO
        title={organization.name ? `${organization.name} · Spaces on Spacia` : "Host Profile · Spacia"}
        description={
          organization.name
            ? `Explore spaces and services offered by ${organization.name}${organization.city ? ` in ${organization.city}` : ""}. Book ${totalSpaces} space${totalSpaces === 1 ? "" : "s"} on Spacia.`
            : "Explore spaces and services offered by hosts on Spacia."
        }
        image={organization.logoUrl || undefined}
        url={profileUrl}
        keywords={[organization.name, "spacia", "workspace", "space booking", "host"].filter(Boolean)}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: organization.name || "Host",
            url: profileUrl,
            ...(organization.logoUrl ? { logo: organization.logoUrl } : {}),
          },
          ...(spaceListings.length > 0
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  name: `Spaces by ${organization.name || "Host"}`,
                  itemListElement: spaceListings,
                },
              ]
            : []),
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <div className={expandedCategory ? "mb-6" : "mb-8"}>
          <nav className="flex items-center flex-wrap gap-1.5 text-sm mb-4" aria-label="Breadcrumb">
            {defaultBreadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <ChevronRight className="w-4 h-4 text-slate-300" />}
                {crumb.path ? (
                  <Link to={crumb.path} className="text-slate-500 hover:text-slate-900 transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-slate-900 font-medium">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>

          {!expandedCategory && (
            <PageHeader
              title={organization.name || `Host #${hostId}`}
              totalCount={totalSpaces}
              subtitle={locationLine || "Host profile"}
              rightContent={
                <div className="flex items-center gap-2">
                  {websiteUrl && (
                    <Button variant="outline" size="sm" asChild>
                      <a
                        href={websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Globe className="w-4 h-4" />
                        Website
                      </a>
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={handleShare}>
                    <Share2 className="w-4 h-4" />
                    Share
                  </Button>
                </div>
              }
            />
          )}
        </div>

        {expandedCategory ? (
          <CategorySearchResults
            category={expandedCategory}
            orgId={orgId}
            subtitle={`Browse ${expandedCategory.name} spaces hosted by ${organization.name || `Host #${hostId}`}`}
            onOpenSpace={setBookingSpace}
          />
        ) : (
          <>
            {/* Category sections */}
            {sectionsLoading ? (
              <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <SpaceCardSkeleton key={i} />
                ))}
              </div>
            ) : error ? (
              <section className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl ring-1 ring-slate-200">
                <AlertTriangle className="w-12 h-12 text-amber-400" />
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Something went wrong</h3>
                <p className="mt-2 text-sm text-slate-500 max-w-sm">{error}</p>
                <button
                  onClick={() => setRetryKey(key => key + 1)}
                  className="mt-6 px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
                >
                  Try again
                </button>
              </section>
            ) : sections.length === 0 ? (
              <section className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl ring-1 ring-slate-200">
                <Building2 className="w-12 h-12 text-slate-300" />
                <h3 className="mt-4 text-lg font-semibold text-slate-900">No spaces listed yet</h3>
                <p className="mt-2 text-sm text-slate-500 max-w-sm">
                  {organization.name || "This host"} hasn't published any spaces yet. Check back soon.
                </p>
                <Link
                  to="/filterprops"
                  className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Browse all spaces
                </Link>
              </section>
            ) : (
              sections.map(section => (
                <CategorySection
                  key={section.category.id}
                  category={section.category}
                  spaces={section.spaces}
                  onOpenSpace={setBookingSpace}
                  onExpand={handleExpandCategory}
                />
              ))
            )}
          </>
        )}
      </div>

      {/* Booking modal */}
      {bookingSpace && (
        <ReusableBookingModal
          isOpen={Boolean(bookingSpace)}
          onClose={() => setBookingSpace(null)}
          space={bookingSpace}
          selectedDate={new Date()}
          existingBookings={[]}
          availabilityInstanceIds={[]}
          timeFormat="12h"
          closeOnSuccess={true}
        />
      )}
    </div>
  );
};

export default HostProfilePage;
